import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Link2, Download, Music, Loader2, Play, Eye, Clock, Film, Sparkles,
  Send, MessageCircle, ListChecks, CheckCircle2, XCircle,
} from "lucide-react";
import { resolveVideo, resolveBatch, downloadUrl, coverUrl } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useLang } from "@/context/LangContext";
import DownloadInterstitial from "@/components/DownloadInterstitial";

const fireCatch = () => window.dispatchEvent(new CustomEvent("neon-catch"));

const isTikTokUrl = (u) => /(^|\.)(tiktok\.com|vm\.tiktok\.com|vt\.tiktok\.com)/i.test(u);

const mapResolveError = (e, t) => {
  const d = e?.response?.data?.detail;
  let code = typeof d === "string" ? d : "";
  if (Array.isArray(d)) {
    const m = d[0]?.msg || "";
    code = m.includes("platform_not_supported") ? "platform_not_supported"
      : m.includes("invalid_url") ? "invalid_url" : "";
  }
  const keys = {
    invalid_url: "dl.errInvalid",
    platform_not_supported: "dl.errPlatform",
    content_unavailable_or_private: "dl.errUnavail",
    content_not_found: "dl.errNotFound",
    analysis_timeout: "dl.errTimeout",
    analysis_failed: "dl.errFailed",
    rate_limited: "dl.errRate",
    platform_temporarily_blocked: "dl.errBlocked",
    file_too_large: "dl.errLarge",
  };
  if (keys[code]) return t(keys[code]);
  if (code && !code.includes("_")) return code; // legacy Arabic server messages
  return t("dl.fetchFail");
};

function ProgressBar({ pct }) {
  return (
    <div
      className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden"
      data-testid="download-progress-bar"
    >
      <div
        className="h-full bg-neon drop-shadow-[0_0_8px_rgba(0,229,255,0.7)] transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function Downloader() {
  const { t } = useLang();
  const [mode, setMode] = useState("single");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState(null);
  const [quality, setQuality] = useState("hd");
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({});

  const [pendingDl, setPendingDl] = useState(null); // interstitial gate

  // click → 5s ad interstitial → auto-close → real download starts
  const downloadFile = (id, kind, q, ext = null) => {
    setPendingDl({ id, kind, q, ext });
  };

  const startDownload = async (id, kind, q, ext = null) => {
    const key = `${id}:${kind}:${q}`;
    const fileExt = ext || (kind === "audio" ? "mp3" : "mp4");
    try {
      track("download_started", { platform: "tiktok", kind, quality: q });
      setProgress((p) => ({ ...p, [key]: 1 }));
      const res = await fetch(downloadUrl(id, kind, q));
      if (!res.ok || !res.body) throw new Error("download failed");
      const total = Number(res.headers.get("Content-Length")) || 0;
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total) {
          setProgress((p) => ({
            ...p,
            [key]: Math.min(99, Math.round((received / total) * 100)),
          }));
        }
      }
      const blob = new Blob(chunks, {
        type: fileExt === "mp3" ? "audio/mpeg" : "video/mp4",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `tiktok_${id.slice(-6)}.${fileExt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      setProgress((p) => ({ ...p, [key]: 100 }));
      track("download_completed", { platform: "tiktok", kind, quality: q });
      toast.success(t(kind === "audio" ? "dl.doneA" : "dl.doneV"));
      setTimeout(
        () => setProgress((p) => { const n = { ...p }; delete n[key]; return n; }),
        2500
      );
    } catch (err) {
      setProgress((p) => { const n = { ...p }; delete n[key]; return n; });
      // Server returned an error — never fall back to native navigation here or
      // the browser would save the error page as a file.
      if (err && err.message === "download failed") {
        track("download_error", { platform: "tiktok", kind, stage: "server" });
        toast.error(t("dl.dlFail"));
        return;
      }
      // fetch/blob stream failed (mobile memory limits) with a healthy server —
      // let the browser's native download manager handle it instead.
      try {
        const a = document.createElement("a");
        a.href = downloadUrl(id, kind, q);
        a.download = `tiktok_${id.slice(-6)}.${fileExt}`;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        track("download_completed", { platform: "tiktok", kind, quality: q, via: "native" });
        toast.success(t(kind === "audio" ? "dl.doneA" : "dl.doneV"));
      } catch {
        track("download_error", { platform: "tiktok", kind, stage: "download" });
        toast.error(t("dl.dlFail"));
      }
    }
  };

  const handleResolve = async () => {
    if (!url.trim()) {
      toast.error(t("dl.pasteFirst"));
      return;
    }
    fireCatch();
    setLoading(true);
    setMedia(null);
    const isTikTok = isTikTokUrl(url.trim());
    track("analysis_started", { platform: isTikTok ? "tiktok" : "unknown" });
    try {
      const data = await resolveVideo(url.trim());
      setMedia(data);
      if (!data.hd_available) setQuality("sd");
      track("analysis_completed", { platform: "tiktok" });
      toast.success(t("dl.caught"));
    } catch (e) {
      track("download_error", { stage: "analysis" });
      toast.error(mapResolveError(e, t));
    } finally {
      setLoading(false);
    }
  };

  const handleBulk = async () => {
    const urls = bulkText.split(/\n+/).map((s) => s.trim()).filter(Boolean).slice(0, 10);
    if (!urls.length) {
      toast.error(t("dl.bulkNeed"));
      return;
    }
    fireCatch();
    setBulkLoading(true);
    setResults([]);
    try {
      const res = await resolveBatch(urls);
      setResults(res);
      const ok = res.filter((r) => r.ok).length;
      if (ok > 0) {
        toast.success(t("dl.bulkCaught").replace("{ok}", ok).replace("{n}", res.length));
      } else {
        toast.error(t("dl.bulkNone"));
      }
    } catch {
      toast.error(t("dl.serverFail"));
    } finally {
      setBulkLoading(false);
    }
  };

  const shareText = media ? encodeURIComponent(`${media.title} — SS Downloader`) : "";
  const shareUrl = media ? encodeURIComponent(media.source_url || "") : "";
  const busy = (id) => Object.keys(progress).some((k) => k.startsWith(`${id}:`));
  const progressKeys = (id) => Object.keys(progress).filter((k) => k.startsWith(`${id}:`));
  const detected = mode === "single" && url.trim() ? isTikTokUrl(url.trim()) : false;

  return (
    <section id="downloader" className="relative py-24 lg:py-32" data-testid="downloader-section">
      <div className="mx-auto max-w-4xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-sm tracking-[0.25em] text-neon font-bold mb-4">{t("dl.kicker")}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-8">
            {t("dl.title1")} <span className="text-neon">{t("dl.title2")}</span>
          </h2>

          <div className="mb-6 inline-flex rounded-full border border-white/15 bg-surface/60 p-1" data-testid="mode-toggle">
            <button
              data-testid="mode-single-button"
              onClick={() => setMode("single")}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200 active:scale-95 ${
                mode === "single" ? "bg-neon text-white" : "text-muted-foreground hover:text-ice"
              }`}
            >
              {t("dl.single")}
            </button>
            <button
              data-testid="mode-bulk-button"
              onClick={() => setMode("bulk")}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200 active:scale-95 flex items-center gap-1.5 ${
                mode === "bulk" ? "bg-neon text-white" : "text-muted-foreground hover:text-ice"
              }`}
            >
              <ListChecks className="h-4 w-4" />
              {t("dl.bulk")}
            </button>
          </div>

          {mode === "single" ? (
            <div className="rounded-2xl border border-white/10 bg-surface/80 backdrop-blur-xl p-3 animate-glow-pulse">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Link2 className="absolute end-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input
                    data-testid="tiktok-url-input"
                    dir="ltr"
                    type="url"
                    inputMode="url"
                    aria-label="رابط الفيديو"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleResolve()}
                    placeholder={t("dl.pastePh")}
                    className="w-full rounded-xl bg-void/80 border border-white/10 py-4 pe-12 ps-4 text-left text-ice placeholder:text-muted-foreground/60 outline-none focus:border-neon transition-colors duration-200"
                  />
                </div>
                <button
                  data-testid="fetch-video-button"
                  onClick={handleResolve}
                  disabled={loading}
                  className="rounded-xl bg-neon px-8 py-4 font-bold text-white flex items-center justify-center gap-2 drop-shadow-[0_0_15px_rgba(0,229,255,0.6)] transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                  {loading ? t("dl.catching") : t("dl.catch")}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-surface/80 backdrop-blur-xl p-4 animate-glow-pulse">
              <textarea
                data-testid="bulk-urls-input"
                dir="ltr"
                rows={5}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"https://www.tiktok.com/@user/video/1...\nhttps://www.tiktok.com/@user/video/2...\nhttps://vm.tiktok.com/xyz..."}
                className="w-full rounded-xl bg-void/80 border border-white/10 p-4 text-left text-sm text-ice placeholder:text-muted-foreground/50 outline-none focus:border-neon transition-colors duration-200 resize-y"
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">{t("dl.bulkHint")}</p>
                <button
                  data-testid="bulk-fetch-button"
                  onClick={handleBulk}
                  disabled={bulkLoading}
                  className="rounded-xl bg-neon px-7 py-3 font-bold text-white flex items-center gap-2 drop-shadow-[0_0_15px_rgba(0,229,255,0.6)] transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60"
                >
                  {bulkLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                  {bulkLoading ? t("dl.catchingAll") : t("dl.catchAll")}
                </button>
              </div>
            </div>
          )}

          {mode === "single" && url.trim() && (
            <p
              className={`mt-3 text-sm font-bold flex items-center gap-1.5 ${detected ? "text-webcyan" : "text-muted-foreground"}`}
              data-testid="platform-detected"
            >
              {detected ? t("dl.detected") : t("dl.supportedHint")}
            </p>
          )}

          <AnimatePresence>
            {mode === "single" && media && (
              <motion.div
                key={media.id}
                initial={{ opacity: 0, y: 34, scale: 0.94, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformPerspective: 900 }}
                className="mt-8 rounded-2xl border border-neon/30 bg-surface/80 backdrop-blur-xl overflow-hidden neon-glow"
                data-testid="video-preview-card"
              >
                <div className="grid sm:grid-cols-[200px_1fr]">
                  <div className="relative h-56 sm:h-auto bg-void">
                    {media.cover_available ? (
                      <img
                        src={coverUrl(media.id)}
                        alt={`غلاف مقطع تيك توك: ${media.title}`}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                        data-testid="video-cover-image"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center text-muted-foreground">
                        <Film className="h-10 w-10" />
                      </div>
                    )}
                    <div className="absolute inset-0 grid place-items-center">
                      <span className="grid place-items-center h-14 w-14 rounded-full bg-black/50 backdrop-blur border border-white/30">
                        <Play className="h-6 w-6 text-white" />
                      </span>
                    </div>
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3 className="font-bold text-lg leading-snug line-clamp-2" data-testid="video-title">
                      {media.title}
                    </h3>
                    <p className="mt-1 text-sm text-webcyan" data-testid="video-author">@{media.author}</p>
                    <div className="mt-3 flex items-center gap-5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> {media.duration} {t("dl.secs")}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Eye className="h-3.5 w-3.5" /> {Number(media.plays).toLocaleString()} {t("dl.views")}
                      </span>
                    </div>

                    <div className="mt-6 flex items-center gap-2" data-testid="quality-selector">
                      <span className="text-xs text-muted-foreground me-2">{t("dl.quality")}</span>
                      <button
                        data-testid="quality-hd-button"
                        onClick={() => setQuality("hd")}
                        disabled={!media.hd_available}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all duration-200 active:scale-95 disabled:opacity-40 ${
                          quality === "hd"
                            ? "bg-neon text-white drop-shadow-[0_0_10px_rgba(0,229,255,0.5)]"
                            : "border border-white/20 text-muted-foreground hover:border-webcyan"
                        }`}
                      >
                        {t("dl.hd")}
                      </button>
                      <button
                        data-testid="quality-sd-button"
                        onClick={() => setQuality("sd")}
                        disabled={!media.sd_available}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all duration-200 active:scale-95 disabled:opacity-40 ${
                          quality === "sd"
                            ? "bg-neon text-white drop-shadow-[0_0_10px_rgba(0,229,255,0.5)]"
                            : "border border-white/20 text-muted-foreground hover:border-webcyan"
                        }`}
                      >
                        {t("dl.sd")}
                      </button>
                    </div>

                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <button
                        data-testid="download-video-button"
                        onClick={() => downloadFile(media.id, "video", quality)}
                        disabled={busy(media.id)}
                        className="flex-1 rounded-xl bg-neon px-6 py-3.5 font-bold text-white flex items-center justify-center gap-2 drop-shadow-[0_0_15px_rgba(0,229,255,0.6)] transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                      >
                        <Download className="h-5 w-5" />
                        {t("dl.dlVideo")} ({quality === "hd" ? "HD" : "SD"})
                      </button>
                      <button
                        data-testid="download-audio-button"
                        onClick={() => downloadFile(media.id, "audio", "audio", "mp3")}
                        disabled={!media.audio_available || busy(media.id)}
                        className="rounded-xl border border-webcyan/60 px-6 py-3.5 font-bold text-webcyan flex items-center justify-center gap-2 transition-colors duration-200 hover:bg-webcyan/10 active:scale-95 disabled:opacity-40"
                      >
                        <Music className="h-5 w-5" />
                        {t("dl.dlAudio")}
                      </button>
                    </div>

                    {progressKeys(media.id).map((k) => {
                      const isAudio = k.split(":")[1] === "audio";
                      return (
                        <div key={k} className="mt-4" data-testid={`download-progress-${k.split(":").slice(1).join("-")}`}>
                          <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                            <span>{isAudio ? t("dl.progA") : t("dl.progV")}</span>
                            <span className="text-neon font-bold">{progress[k]}%</span>
                          </div>
                          <ProgressBar pct={progress[k]} />
                        </div>
                      );
                    })}

                    <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                      <span className="text-xs text-muted-foreground">{t("dl.share")}</span>
                      <a
                        data-testid="share-whatsapp-button"
                        href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold text-ice/80 transition-colors duration-200 hover:border-green-500 hover:text-green-400 active:scale-95"
                      >
                        <MessageCircle className="h-4 w-4" /> WhatsApp
                      </a>
                      <a
                        data-testid="share-telegram-button"
                        href={`https://t.me/share/url?url=${shareUrl}&text=${shareText}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold text-ice/80 transition-colors duration-200 hover:border-webcyan hover:text-webcyan active:scale-95"
                      >
                        <Send className="h-4 w-4" /> Telegram
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {mode === "bulk" && results.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 rounded-2xl border border-white/10 bg-surface/80 backdrop-blur-xl divide-y divide-white/5 overflow-hidden"
                data-testid="bulk-results-list"
              >
                {results.map((r, i) => (
                  <div key={i} className="p-5" data-testid={`bulk-result-${i}`}>
                    {r.ok ? (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-neon" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate">{r.media.title}</p>
                          <p className="text-xs text-webcyan">@{r.media.author}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const vid = {
                              kind: "video",
                              id: r.media.hd_available ? "hd" : "sd",
                              label: r.media.hd_available ? "HD" : "SD",
                              ext: "mp4",
                            };
                            const aud = r.media.audio_available
                              ? { kind: "audio", id: "audio", label: "MP3", ext: "mp3" }
                              : null;
                            return (
                              <>
                                {vid && (
                                  <button
                                    data-testid={`bulk-download-${i}`}
                                    onClick={() => downloadFile(r.media.id, vid.kind, vid.id, vid.ext)}
                                    disabled={busy(r.media.id)}
                                    className="rounded-full bg-neon px-4 py-2 text-xs font-bold text-white flex items-center gap-1.5 transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-60"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    {vid.label}
                                  </button>
                                )}
                                {aud && (
                                  <button
                                    data-testid={`bulk-audio-${i}`}
                                    onClick={() => downloadFile(r.media.id, "audio", aud.id, "mp3")}
                                    disabled={busy(r.media.id)}
                                    className="rounded-full border border-webcyan/60 px-4 py-2 text-xs font-bold text-webcyan flex items-center gap-1.5 transition-colors duration-200 hover:bg-webcyan/10 active:scale-95 disabled:opacity-40"
                                  >
                                    <Music className="h-3.5 w-3.5" /> {aud.label || "MP3"}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        {progressKeys(r.media.id).map((k) => (
                          <div key={k} className="sm:basis-full" data-testid={`bulk-progress-${i}-${k.split(":").slice(1).join("-")}`}>
                            <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                              <span>{t("dl.progShort")}</span>
                              <span className="text-neon font-bold">{progress[k]}%</span>
                            </div>
                            <ProgressBar pct={progress[k]} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <XCircle className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground truncate" dir="ltr">{r.url}</p>
                          <p className="text-xs text-neon/90 mt-0.5">{mapResolveError({ response: { data: { detail: r.error } } }, t)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
      <DownloadInterstitial
        open={!!pendingDl}
        onClose={() => {
          const p = pendingDl;
          setPendingDl(null);
          if (p) startDownload(p.id, p.kind, p.q, p.ext);
        }}
      />
    </section>
  );
}
